import React, { forwardRef, useMemo, type ChangeEvent } from 'react';

import classnames from 'classnames';
import { usePhoneInput, type ParsedCountry } from 'react-international-phone';

import { useMergeRefs } from '../../hooks/useMergeRefs';
import { Input, type InputProps } from '../Input';

import { FlagSelect } from './primitives/FlagSelect/FlagSelect';
import { flagSelectCountries } from './primitives/FlagSelect/FlagSelect.data';

import styles from './PhoneNumberInput.module.scss';

const hasValidValue = (phone: string, country: ParsedCountry, forceDialCode: boolean) => {
    const hasPhoneNumber = phone && phone !== `+${country.dialCode}`;
    return Boolean((!forceDialCode && phone) || (forceDialCode && hasPhoneNumber));
};

export type PhoneNumberChangeInfo = {
    inputValue: string;
    dialCode: string;
};

export type ChangePhoneNumberHandler = (phone: string, info: PhoneNumberChangeInfo) => void;

export type PhoneNumberInputProps = {
    value?: string | undefined;
    preferredCountries?: readonly string[];
    defaultCountry?: string;
    forceDialCode?: boolean;
    onPhoneNumberChange?: ChangePhoneNumberHandler;
} & Omit<InputProps, 'type' | 'value'>;

export const PhoneNumberInput = forwardRef<HTMLInputElement, PhoneNumberInputProps>(
    (props: PhoneNumberInputProps, ref) => {
        const {
            defaultCountry,
            onChange,
            onPhoneNumberChange,
            readOnly,
            forceDialCode = false,
            disabled,
            preferredCountries,
            autoFocus,
            className,
            value,
            allowClear,
            ...rest
        } = props;

        const { inputValue, handlePhoneValueChange, inputRef, country, setCountry, phone } =
            usePhoneInput({
                defaultCountry: defaultCountry?.toString().toLowerCase(),
                value: value ?? undefined,
                disableDialCodePrefill: !defaultCountry && !forceDialCode,
                countries: flagSelectCountries,
                forceDialCode,
                onChange: (data) => {
                    onPhoneNumberChange?.(data.phone, {
                        inputValue: data.inputValue,
                        dialCode: data.country.dialCode
                    });
                }
            });

        const mergedRef = useMergeRefs([ref, inputRef]);

        const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
            handlePhoneValueChange(e);
            onChange?.(e);
        };

        const flagSelectAsPrefix = useMemo(() => {
            return (
                <FlagSelect
                    value={country.iso2}
                    onSelect={(countryIso2) => setCountry(countryIso2)}
                    preferredCountries={preferredCountries}
                    readOnly={readOnly}
                    disabled={disabled}
                />
            );
        }, [country.iso2, preferredCountries, readOnly, disabled]);

        return (
            <Input
                ref={mergedRef}
                className={classnames(styles.inputWrapper, className)}
                inputClassName={styles.input}
                readOnly={readOnly}
                disabled={disabled}
                autoFocus={autoFocus}
                prefix={readOnly && !value ? null : flagSelectAsPrefix}
                allowClear={allowClear ?? hasValidValue(phone, country, forceDialCode)}
                value={inputValue}
                onChange={handleInputChange}
                {...rest}
            />
        );
    }
);

PhoneNumberInput.displayName = 'PhoneNumberInput';
